import { Message } from '@nextforce/api'
import { Icon, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { Image } from 'expo-image'
import {
  isImageType,
  isPdfType,
  isVideoType,
} from 'file_folder_repository/util'
import React from 'react'
import { View } from 'react-native'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { stripInvalidCodepoints } from 'shared/utils'
import { useUser } from 'users/queries/detail'

interface ReplyMessageProps {
  replyMessage?: Message
  setReplyMessage?: React.Dispatch<React.SetStateAction<Message | undefined>>
}

export const REPLY_MESSAGE_BAR_HEIGHT = 50

export function ReplyMessage({
  setReplyMessage,
  replyMessage,
}: ReplyMessageProps) {
  const styles = useStyles()
  const { data: user } = useUser()
  const isSameUserReply = replyMessage?.user.id === user?.id

  return (
    <View style={styles.container}>
      <View style={styles.replyIndicator} />
      <MessageContent
        isSameUserReply={isSameUserReply}
        replyMessage={replyMessage}
        attachment={replyMessage?.attachment}
      />
      {setReplyMessage ? (
        <CloseButton onPress={() => setReplyMessage(undefined)} />
      ) : null}
    </View>
  )
}

interface CloseButtonProps {
  onPress: () => void
}

function CloseButton({ onPress }: CloseButtonProps) {
  const styles = useStyles()
  return (
    <PressableOpacity style={styles.crossButton} onPress={onPress}>
      <Icon style={styles.crossButtonIcon} name="close" type="ionicons" />
    </PressableOpacity>
  )
}

interface MessageContentProps {
  isSameUserReply: boolean
  replyMessage?: Message
  attachment?: Message['attachment']
}

function MessageContent({
  isSameUserReply,
  replyMessage,
  attachment,
}: MessageContentProps) {
  const styles = useStyles()
  return (
    <View style={styles.messageContainer}>
      <View style={styles.messageInfoContainer}>
        <Text style={styles.name} textBreakStrategy="simple">
          {isSameUserReply
            ? t('you')
            : stripInvalidCodepoints(
                `${replyMessage?.user?.firstName ?? ''} ${
                  replyMessage?.user?.lastName ?? ''
                }`
              )}
        </Text>
        <View style={styles.messageBody}>
          {attachment && (
            <Icon
              name={getIconName(attachment.mimeType)}
              type="font-awesome"
              size={12}
              style={styles.iconStyle}
            />
          )}
          <View style={{ flex: 1 }}>
            <Text
              style={styles.messageText}
              numberOfLines={1}
              textBreakStrategy="simple"
            >
              {stripInvalidCodepoints(replyMessage?.body ?? '')}
            </Text>
          </View>
        </View>
      </View>
      {attachment?.thumbnail && (
        <View>
          <Image
            style={styles.attachmentPreviewImage}
            contentFit="cover"
            source={{ uri: attachment.thumbnail.url }}
          />
        </View>
      )}
    </View>
  )
}

export function getIconName(mimeType?: string) {
  return isImageType(mimeType)
    ? 'photo'
    : isVideoType(mimeType)
    ? 'video-camera'
    : isPdfType(mimeType)
    ? 'file-pdf-o'
    : ''
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    height: REPLY_MESSAGE_BAR_HEIGHT,
  },
  attachmentPreviewImage: {
    width: 50,
    height: 50,
    marginVertical: 5,
    padding: 5,
  },
  messageInfoContainer: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
  },
  replyIndicator: {
    paddingLeft: 5,
    paddingRight: 6,
    borderRightWidth: 2,
    borderRightColor: '#2196F3',
    marginRight: 6,
    height: '100%',
    justifyContent: 'center',
  },
  crossButtonIcon: {
    width: 24,
    height: 24,
  },
  crossButton: {
    padding: 4,
  },
  messageContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingRight: 10,
  },
  iconStyle: {
    marginHorizontal: 4,
    marginVertical: 4,
  },
  messageBody: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  name: {
    fontFamily: font.bold,
  },
  messageText: {
    fontSize: 12,
  },
}))
